import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Params, Router, UrlTree } from '@angular/router';
import { filter } from 'rxjs/operators';
import { STICKY_QUERY_PARAMS } from './sticky-query-params.constants';

@Service()
export class StickyQueryParamsService {
  private readonly router: Router = inject(Router);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly stickyParamsStore: Map<string, string> = new Map<string, string>();

  public init(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationStart): void => this.restoreMissingStickyParams(event));

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd): void => this.captureStickyParams(event.urlAfterRedirects));
  }

  private captureStickyParams(url: string): void {
    const queryParams: Params = this.router.parseUrl(url).queryParams;

    STICKY_QUERY_PARAMS.forEach((param: string) => {
      const value: string = queryParams[param];

      if (value != null) {
        this.stickyParamsStore.set(param, value);
      }
    });
  }

  private restoreMissingStickyParams(event: NavigationStart): void {
    const missingParams: string[] = STICKY_QUERY_PARAMS.filter(
      (param: string) => this.stickyParamsStore.has(param) && !this.hasQueryParamInUrl(event.url, param)
    );

    if (!missingParams.length) {
      return;
    }

    const urlTree: UrlTree = this.router.parseUrl(event.url);

    urlTree.queryParams = missingParams.reduce(
      (queryParams: Params, param: string) => ({ ...queryParams, [param]: this.stickyParamsStore.get(param) }),
      urlTree.queryParams
    );

    void this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  private hasQueryParamInUrl(url: string, param: string): boolean {
    return param in this.router.parseUrl(url).queryParams;
  }
}
